import { makeStyles } from "@material-ui/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import * as React from "react";
import { colors } from "../../theme";
import { Order, getComparator, stableSort } from "./sort";

interface EnhancedTableProps {
    onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
    order: Order;
    orderBy: any;
    headers: any[];
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort, headers } = props;
    const createSortHandler = (property: any) => (
        event: React.MouseEvent<unknown>
    ) => {
        onRequestSort(event, property);
    };

    const theme = useStyles();

    return (
        <TableHead>
            <TableRow>
                {headers.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.headerAlignRight ? "right" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={orderBy === headCell.id ? order : false}
                        className={theme.emphasized}
                        sx={{
                            p: 2,
                            pt: 4,
                            pb: 4,
                        }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                            sx={{
                                width: "fit-content",
                            }}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span">
                                    {order === "desc" ? "" : ""}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: colors.white.main,
    },
    highlight: {
        backgroundColor: colors.green.select,
    },
    emphasized: {
        fontWeight: "bold",
    },
}));

interface RowProps {
    row: any;
    rowIdField: string;
    headers: any[];
    onRowClickMethod?: any;
    changeColorOnSelect?: boolean;
    highlightOnHover?: boolean;
    selected: boolean;
    narrowRow: boolean;
}

export interface HeadCell {
    disablePadding?: boolean;
    narrowRow?: boolean;
    id: string;
    label: string;
    numeric: boolean;
    displayField?: string;
    grow?: number;
    wrap?: boolean;
    align?: string;
    headerAlignRight?: boolean;
    format?: any;
    cell?: any;
    unClickable?: boolean;
}

function Row(props: RowProps) {
    const { row } = props;
    const { selected, highlightOnHover } = props;
    const theme = useStyles();

    // color entire row by predefined style
    // let className = selected ? theme.highlight : theme.root;
    let className = theme.root;

    if (props.changeColorOnSelect) {
        className = selected ? theme.highlight : theme.root;
    }

    return (
        <React.Fragment>
            <TableRow
                className={className}
                sx={{ "& > *": { borderBottom: "unset" }, p: 1 }}
                hover={highlightOnHover}
            >
                {props.headers.map((x: HeadCell, index: number) => {
                    if (!Object.keys(row).includes(x.id)) {
                        return;
                    }
                    var val;
                    if (x.displayField) {
                        val = row[x.displayField];
                    } else {
                        val = row[x.id];
                    }

                    if (x.cell) {
                        val = x.cell(row);
                    } else if (x.format) {
                        val = x.format(val, row);
                    } else if (!val) {
                        val = " ";
                    } else if (val === 0) {
                        val = "0";
                    }

                    const alignCategory = x.numeric ? "right" : "left";
                    const padAmount = props.narrowRow ? 0 : 3;
                    // some cells should not be clickable
                    // so we don't assign onClick method by row
                    const onClickMethod = x.unClickable
                        ? null
                        : props.onRowClickMethod;
                    return (
                        <TableCell
                            key={`valtype_${index}`}
                            align={alignCategory}
                            sx={{ p: padAmount, pl: 2, pr: 2 }}
                            onClick={onClickMethod}
                        >
                            {val}
                        </TableCell>
                    );
                })}
            </TableRow>
        </React.Fragment>
    );
}

export type ChangeSortCond = (key: string, order: Order) => void;
export type ChangePageCond = (no: number, numRows: number) => void;
export interface DisplayCond {
    sort: {
        key: string;
        order: Order;
    };
    page: {
        no: number;
        numRows: number;
    };
}
export function newDisplayCond(
    key: string,
    order: Order,
    no: number,
    numRows: number
): DisplayCond {
    return {
        sort: {
            key: key,
            order: order,
        },
        page: {
            no: no,
            numRows: numRows,
        },
    };
}

interface SortableTableProps {
    tableHeaders: any[];
    tableData: any[];
    tableProps: any;
    displayCond: DisplayCond;
    rowIdField: string;
    onRowClickMethod?: any;
    changeRowColorOnSelect?: boolean;
    highlightOnHover?: boolean;
    slimRows?: boolean;
    footer?: any;
    callbackChangeSortCond?: ChangeSortCond;
    callbackChangePageCond?: ChangePageCond;
}

const findFirstPreselectedRowId = (
    rows: any[],
    col: string,
    preselectedVal: any,
    idCol: any
) => {
    if (!rows) {
        return "";
    }
    let preselectedRowId = "";
    for (let x = 0; x < rows.length; x++) {
        if (rows[x][col] == preselectedVal) {
            preselectedRowId = rows[x][idCol];
        }
    }
    return preselectedRowId;
};

export const SortableTable: React.FC<SortableTableProps> = (
    props: SortableTableProps
) => {
    const changeRowColorOnSelect = false || props.changeRowColorOnSelect;
    let highlightOnHover = true;
    if (props.highlightOnHover !== undefined) {
        highlightOnHover = props.highlightOnHover;
    }

    const dc = props.displayCond;
    const [order, setOrder] = React.useState<Order>(dc.sort.order);
    const [orderBy, setOrderBy] = React.useState<string>(dc.sort.key);
    const [page, setPage] = React.useState(dc.page.no);
    const [rowsPerPage, setRowsPerPage] = React.useState(dc.page.numRows);
    const slimRows = props.slimRows ? true : false;

    const preselectedRow = findFirstPreselectedRowId(
        props.tableData,
        "selected",
        "yes",
        props.rowIdField
    );
    const [selectedRowId, setSelectedRowId] = React.useState<string>(
        preselectedRow
    );

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string
    ) => {
        const isAsc = orderBy === property && order === "asc";
        const no = isAsc ? "desc" : "asc";
        setOrder(no);
        setOrderBy(property);
        if (props.callbackChangeSortCond) {
            props.callbackChangeSortCond(property, no);
        }
    };

    const emptyRows =
        page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - props.tableData.length)
            : 0;

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        if (props.callbackChangePageCond) {
            props.callbackChangePageCond(newPage, rowsPerPage);
        }
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const nr = parseInt(event.target.value, 10);
        setRowsPerPage(nr);
        setPage(0);
        if (props.callbackChangePageCond) {
            props.callbackChangePageCond(0, nr);
        }
    };

    const handleSelectAllClick = () => {};

    const footerElement = props.footer ? (
        <TableFooter>
            <Row
                row={props.footer}
                headers={props.tableHeaders}
                rowIdField={props.rowIdField}
                selected={false}
                narrowRow={slimRows}
            />
        </TableFooter>
    ) : (
        <Box></Box>
    );

    return (
        <Box sx={{ width: "100%" }}>
            <TableContainer
                sx={{ width: "100%", cursor: "pointer" }}
                component={Paper}
            >
                <Table aria-label="collapsible table">
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        headers={props.tableHeaders}
                    />
                    <TableBody>
                        {stableSort(
                            props.tableData,
                            getComparator(order, orderBy)
                        )
                            .slice(
                                page * rowsPerPage,
                                page * rowsPerPage + rowsPerPage
                            )
                            .map((row, index) => {
                                const rowId = row[props.rowIdField];
                                const selected = rowId == selectedRowId;

                                return (
                                    <Row
                                        key={`row_${rowId}`}
                                        row={row}
                                        headers={props.tableHeaders}
                                        rowIdField={props.rowIdField}
                                        selected={selected}
                                        onRowClickMethod={() => {
                                            setSelectedRowId(rowId);
                                            props.onRowClickMethod(row);
                                        }}
                                        changeColorOnSelect={
                                            changeRowColorOnSelect
                                        }
                                        highlightOnHover={highlightOnHover}
                                        narrowRow={slimRows}
                                    />
                                );
                            })}
                        {emptyRows > 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={props.tableHeaders.length}
                                />
                            </TableRow>
                        )}
                    </TableBody>
                    {footerElement}
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[
                    props.displayCond.page.numRows,
                    50,
                    { label: "全て", value: props.tableData.length },
                ]}
                component="div"
                colSpan={props.tableHeaders.length}
                count={props.tableData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage="表示件数"
                SelectProps={{
                    inputProps: {
                        "aria-label": "件数",
                    },
                    native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};
